import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { UserPageContext } from "../../App";
import Swal from "sweetalert2";
import "./AllAdmins.css";
import Pagination from "../Pagination";
import CustomSelect from "../CustomSelectField/CustomSelect";

const AllAdminsLayer = () => {
  const [records, setRecords] = useState([]);
  const [data, setData] = useState(null);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [update, setUpdate] = useState(0);
  const { userPage, setUserPage } = useContext(UserPageContext);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("user");
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const debounceFunction = setTimeout(() => {
      if (search.trim() !== "") {
        setQuery(search);
        setUserPage(0); // Reset to first page when searching
      } else {
        setQuery("");
      }
    }, 500);
    return () => clearTimeout(debounceFunction);
  }, [search]);

  useEffect(() => {
    const fetchAllAdmins = async () => {
      try {
        setLoading(true);
        const searchParams = new URLSearchParams({
          page: userPage,
          role: "user",
          limit: limit,
          search: query, // Use the debounced query value
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/users?${searchParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const json = await response.json();

        if (response.ok) {
          const sortedData = json.data.users.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          );
          setRecords(sortedData);
          setData(sortedData);

          setTotal(json.data.total);
          setHasNext(json.data.hasNext);
          setHasPrev(json.data.hasPrev);
          setLimit(json.data.limit);
        } else {
          setRecords([]);
        }
      } catch (error) {
        console.error("Error fetching admins:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllAdmins();
  }, [update, limit, userPage, role, query]);

  const handleDelete = (myId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-success btn-sm ms-2",
        cancelButton: "btn btn-sm",
        popup: "my-custom-modal",
        title: "my-custom-title",
        icon: "my-custom-icon",
        content: "my-custom-text",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}api/admin/users/${myId}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (response.ok) {
            setUpdate(update + 1);
            Swal.fire({
              title: "Deleted!",
              text: "Role has been deleted.",
              icon: "success",
            });
          }
        } catch (error) {
          console.error("Error deleting role:", error);
          Swal.fire({
            title: "Error",
            text: "Failed to delete role",
            icon: "error",
          });
        }
      }
    });
  };

  const handleEdit = (id) => {
    navigate("/edit-user/" + id);
  };

  const totalPages = Math.ceil(total / limit);
  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="icon-field">
            <input
              type="text"
              className="form-control form-control-sm w-auto"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="icon">
              <Icon icon="ion:search-outline" />
            </span>
          </div>
        </div>
        {/* <div className="d-flex flex-wrap align-items-center gap-3">
          <select
            className="form-select form-select-sm w-auto"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              setUserPage(0);
            }}
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="garage_owner">Garage Owner</option>
          </select>
        
        </div> */}
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            {/* Table headers and rows remain the same */}
            <thead>
              <tr>
                {/* <th scope="col">ID</th> */}
                <th scope="col">Name</th>
                <th scope="col">Mobile</th>
                <th scope="col">Role</th>
                <th scope="col" className="text-center">
                  Status
                </th>
                <th scope="col">Date Created</th>
                <th scope="col" className="text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    No users found
                  </td>
                </tr>
              ) : (
                records.map((user) => (
                  <tr
                    key={user.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/edit-user/${user.id}`)}
                  >
                    {/* <td>{user.id}</td> */}
                    <td>
                      <div className="d-flex align-items-center">
                        <img
                          src={
                            user.image_url ||
                            "assets/images/user-list/user-list1.png"
                          }
                          alt="User"
                          className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden"
                        />
                        <div>

                        <span className="user-name-truncate text-md mb-0 fw-normal text-secondary-light">
                          {user.fullName}
                        </span>
                        <span>{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>{user.phoneNumber || "-"}</td>
                    <td>{user.role}</td>
                    <td className="d-inline-block text-center">
                      <span
                        className={`${user.isActive ? "bg-success-focus text-success-600 border-success-main" : "bg-neutral-200 text-neutral-600 border-neutral-400"} border px-24 py-4 radius-4 fw-medium text-sm`}
                        style={{ width: "120px" }}
                      >
                        {user.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{format(new Date(user.createdAt), "dd MMM, yyyy")}</td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(user.id);
                          }}
                          className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          {/* <Icon icon="lucide:edit" className="menu-icon" /> */}
                          <Icon icon="lucide:eye" className="menu-icon" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(user.id);
                          }}
                          className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon
                            icon="fluent:delete-24-regular"
                            className="menu-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <div className="d-flex align-items-center gap-2">
            <span>
              Showing {records.length} of {total} entries
            </span>

            <CustomSelect
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setUserPage(0);
              }}
              options={[
                { value: "10", label: "10" },
                { value: "15", label: "15" },
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
            />
          </div>

          <Pagination
            currentPage={userPage}
            totalPages={totalPages}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onPageChange={setUserPage}
          />
        </div>
      </div>
    </div>
  );
};

export default AllAdminsLayer;
